/**
 * The invite a person scanned (heyportal.app/i/{code}) — kept until their account exists.
 *
 * Two homes, on purpose: a first-party cookie (30 days) because that is what survives
 * sign-up, email verification and an OAuth round trip through Google or Apple; and
 * localStorage for the landing path, which the cookie is too small a place for.
 * `inviteRefCode()` is what the auth calls send as `ref`; the server records the
 * referral once, when the account comes into being.
 */
const COOKIE = 'pr_ref';
const KEY = 'pr.invite';
const DAYS = 30;

const CODE_OK = /^[a-z0-9]{4,16}$/;

export function normalizeInviteCode(code) {
  const c = String(code || '').trim().toLowerCase();
  return CODE_OK.test(c) ? c : '';
}

function readCookie() {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
    return m ? normalizeInviteCode(decodeURIComponent(m[1])) : '';
  } catch { return ''; }
}

function writeCookie(code) {
  try {
    const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=${encodeURIComponent(code)}; Max-Age=${DAYS * 86400}; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies off */ }
}

export function rememberInvite({ code, path }) {
  const c = normalizeInviteCode(code);
  if (!c) return;
  writeCookie(c);
  try { localStorage.setItem(KEY, JSON.stringify({ code: c, path: path || null, at: Date.now() })); } catch { /* storage off */ }
}

/** `{ code, path }` or null. The path is where the person lands after sign-up. */
export function readInvite() {
  let stored = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw);
      if (v && normalizeInviteCode(v.code) && (!v.at || Date.now() - v.at < DAYS * 86400 * 1000)) stored = { code: normalizeInviteCode(v.code), path: v.path || null };
    }
  } catch { /* ignore */ }
  const cookie = readCookie();
  if (stored && (!cookie || cookie === stored.code)) return stored;
  if (cookie) return { code: cookie, path: stored?.code === cookie ? stored.path : null };
  return null;
}

/** The code to send with a sign-up, or undefined. */
export function inviteRefCode() {
  return readInvite()?.code || undefined;
}

export function clearInvite() {
  try { document.cookie = `${COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`; } catch { /* ignore */ }
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
